import { Global, css } from '@emotion/react'
import { Link } from 'gatsby'
import { type ComponentProps, Fragment, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { toSlug } from '@/helpers'
import { useElementHeight } from '@/hooks/useElementRect'
import { dropdownArrow, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentProps<'nav'> {
  data: Queries.DivisionPageQuery['datoCmsDivisionPage']
}

export const DivisionSubnav = ({ data, ...props }: Props) => {
  const [navOpen, setNavOpen] = useState(false)
  const [heightRef, setHeightRef] = useState<HTMLDivElement | null>(
    null
  )
  const height = useElementHeight(heightRef)
  const { inView, ref: inViewRef } = useInView({
    rootMargin: '0% 0% 100%',
  })
  const anchorLinks = [
    'Overview',
    'Curriculum',
    'Beyond the Classroom',
    data?.contact ? 'Contact' : undefined,
  ]
  const styles = {
    nav: css`
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      position: sticky;
      top: calc(var(--nav-height-min) - 1px);
      z-index: 10;
      column-gap: 1.875em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 0.333em var(--margin);
      margin: calc(2.25em - 0.5rem) 0 2.25em;
      font-size: var(--fs-15);
      line-height: 1;
      ${mq().ms} {
        display: grid;
        justify-content: stretch;
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 1px;
        background: rgba(0, 0, 0, 0.125);
        opacity: 0;
        transition: opacity 300ms ease;
        ${!inView &&
        css`
          opacity: 1;
        `}
      }
    `,
    global: css`
      :root {
        --subnav-height: calc(1.666 * var(--fs-15));
        ${mq().ms} {
          --subnav-height: calc(1.666 * var(--fs-16));
        }
      }
    `,
    jumpTo: css`
      color: ${colors.gray65};
      ${mq().ms} {
        text-align: center;
      }
    `,
    button: css`
      display: none;
      ${mq().ms} {
        display: inline-block;
        font-style: italic;
        font-size: var(--fs-16);
        color: ${colors.gray45};
        @media (hover: hover) {
          &:hover {
            color: ${colors.red};
          }
        }
      }
    `,
    linksOuter: css`
      display: contents;
      ${mq().ms} {
        display: block;
        height: 0;
        overflow: hidden;
        transition: height 300ms ease;
        ${navOpen &&
        css`
          height: ${height}px;
        `};
      }
    `,
    links: css`
      display: flex;
      column-gap: 2em;
      ${mq().ms} {
        display: grid;
        justify-items: center;
        text-align: center;
        row-gap: 1em;
        padding: 1em 0 0.25em;
      }
    `,
    link: css`
      text-decoration: none;
      color: ${colors.red};
      font-weight: 500;
      @media (hover: hover) {
        &:hover {
          color: ${colors.redDark};
        }
      }
    `,
    divider: css`
      height: 1px;
      width: 100%;
      background: rgba(0, 0, 0, 0.1);
    `,
  }

  return (
    <Fragment>
      <nav
        css={styles.nav}
        {...props}
      >
        <Global styles={styles.global} />
        <div css={styles.jumpTo}>
          Jump to:{' '}
          <button
            css={styles.button}
            onClick={() => {
              setNavOpen(prev => !prev)
            }}
          >
            Select
            <div css={dropdownArrow(navOpen)} />
          </button>
        </div>
        <div css={styles.linksOuter}>
          <div
            ref={node => setHeightRef(node)}
            css={styles.links}
          >
            {anchorLinks.map(label => {
              return (
                <Link
                  css={styles.link}
                  to={`#${toSlug(label)}`}
                  key={label}
                  onClick={() => setNavOpen(false)}
                >
                  {label}
                </Link>
              )
            })}
          </div>
        </div>
      </nav>
      <div
        css={styles.divider}
        ref={inViewRef}
      />
    </Fragment>
  )
}
